import { api } from '@/lib/api/client'

/** The layouts a view can be drawn as. Table and calendar are not built yet. */
export type ViewLayout = 'board' | 'list' | 'table' | 'timeline' | 'calendar'

/**
 * The filter tree the server compiles.
 *
 * A leaf names a field, an operator and a value; a branch joins leaves with
 * `and` or `or`. The field list is a closed enum on the server — a name that is
 * not one of its cases never reaches a query — so the editor offers exactly
 * those and nothing else.
 */
export type FilterNode =
  | { and: FilterNode[] }
  | { or: FilterNode[] }
  | { field: string; operator: string; value?: unknown }

export interface ViewDefinition {
  scope?: Record<string, unknown>
  filter?: FilterNode
  sort?: { field: string; direction: 'asc' | 'desc' }[]
  groupBy?: string
}

export interface SavedView {
  id: string
  name: string
  slug: string
  layout: ViewLayout
  projectId: string | null
  projectKey: string | null
  isShared: boolean
  isMine: boolean
  ownerId: string | null
  position: number
  definition: ViewDefinition | null
  canEdit: boolean
  updatedAt: string | null
}

export async function fetchViews(params: { project?: string } = {}): Promise<SavedView[]> {
  const { data } = await api.get<{ data: SavedView[] }>('/views', { params })

  return data.data
}

export async function createView(input: {
  name: string
  layout: ViewLayout
  projectId?: string | null
  isShared?: boolean
  definition: ViewDefinition
}): Promise<SavedView> {
  const { data } = await api.post<{ data: SavedView }>('/views', input)

  return data.data
}

export async function updateView(
  id: string,
  changes: { name?: string; isShared?: boolean; definition?: ViewDefinition; layout?: ViewLayout },
): Promise<SavedView> {
  const { data } = await api.patch<{ data: SavedView }>(`/views/${id}`, changes)

  return data.data
}

export async function deleteView(id: string): Promise<void> {
  await api.delete(`/views/${id}`)
}

/** Every field the server will filter on, in the order a person thinks of them. */
export const FILTER_FIELDS = [
  'state_category',
  'state',
  'priority',
  'assignee',
  'type',
  'label',
  'visibility',
  'due_date',
  'start_date',
  'closed_at',
  'created_at',
  'text',
  'project',
  'reporter',
] as const

export const FILTER_OPERATORS = [
  'eq',
  'ne',
  'in',
  'not_in',
  'lt',
  'lte',
  'gt',
  'gte',
  'contains',
  'is_null',
  'is_not_null',
] as const
